import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Keyboard, Menu } from 'lucide-react';
import { useHotkeys } from '@/hooks/useHotkeys';
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery';
import { useNavItems } from '@/routes/useNavItems';
import { Button } from '@/components/ui';
import { ErrorBoundary } from '@/features/errors/ErrorPages';
import { CashOutButton } from '@/features/shifts/CashOutButton';
import { CashPayoutHost } from '@/features/shifts/CashPayoutHost';
import { usePermissions } from '@/hooks/usePermissions';
import { useSettings } from '@/hooks/useSettings';
import { ShiftBannerSlot } from './ShiftBannerSlot';
import { ShortcutHelp } from './ShortcutHelp';
import { Sidebar } from './Sidebar';
import { UserMenu } from './UserMenu';

const SIDEBAR_KEY = 'pos.sidebar.collapsed';

/** Saved choice wins; otherwise tills below 1440px start with the narrow icon rail so screens get the room. */
function readCollapsed(): boolean {
  try {
    const saved = localStorage.getItem(SIDEBAR_KEY);
    if (saved !== null) return saved === '1';
  } catch {
    /* ignore */
  }
  return typeof window !== 'undefined' && window.innerWidth < 1440;
}

/**
 * Authenticated shell: sidebar (docked >= 1024px, collapsible, Alt+N; a hamburger drawer below), topbar
 * (shift banner slot, help, user menu), routed content wrapped in an ErrorBoundary. Screens render INSIDE `<main>`;
 * do not add your own shell. POS-style screens that need the full viewport can wrap their root in
 * `<div className="-m-4 h-[calc(100%+2rem)]">`.
 */
export function AppLayout() {
  const items = useNavItems();
  const { isAdmin, isCashier } = usePermissions();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const desktop = useMediaQuery(DESKTOP_QUERY, true);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    menuButton.current?.focus();
  }, []);

  const toggleSidebar = useCallback(() => {
    if (!desktop) {
      setDrawerOpen((o) => !o);
      return;
    }
    setCollapsed((c) => {
      try {
        localStorage.setItem(SIDEBAR_KEY, c ? '0' : '1');
      } catch {
        /* ignore */
      }
      return !c;
    });
  }, [desktop]);

  const drawerShown = drawerOpen && !desktop;

  // A route change (link click, Alt+N, back button) always dismisses the drawer.
  const [lastPath, setLastPath] = useState(location.pathname);
  if (lastPath !== location.pathname) {
    setLastPath(location.pathname);
    if (drawerOpen) setDrawerOpen(false);
  }

  // Drawer: focus the first link on open, Esc closes, Tab stays inside.
  useEffect(() => {
    if (!drawerShown) return;
    const root = drawerRef.current;
    root?.querySelector<HTMLElement>('nav a')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeDrawer();
      } else if (e.key === 'Tab' && root) {
        const f = [...root.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')];
        if (f.length === 0) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerShown, closeDrawer]);

  useHotkeys(
    [
      ...items
        .filter((i) => i.shortcut)
        .map((i) => ({
          keys: i.shortcut!,
          description: i.label,
          group: 'Navigation',
          handler: () => navigate(i.path),
        })),
      {
        keys: 'alt+b',
        description: 'Collapse / expand sidebar (menu on small screens)',
        group: 'Navigation',
        handler: toggleSidebar,
      },
      {
        keys: '?',
        description: 'Show keyboard shortcuts',
        group: 'General',
        handler: () => setHelpOpen(true),
      },
    ],
    { enabled: !helpOpen },
  );

  return (
    <div className="flex h-full">
      <a
        href="#main"
        onClick={(e) => {
          // Hash links would fight the data router; focus the landmark directly.
          e.preventDefault();
          document.getElementById('main')?.focus();
        }}
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[70] focus:rounded-lg focus:bg-primary focus:px-3 focus:py-2 focus:text-primary-fg"
      >
        Skip to content
      </a>
      {desktop && <Sidebar items={items} collapsed={collapsed} onToggle={toggleSidebar} />}
      {drawerShown && (
        <div
          ref={drawerRef}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
          className="fixed inset-0 z-50 flex"
        >
          <Sidebar
            items={items}
            collapsed={false}
            drawer
            onToggle={closeDrawer}
            onNavigate={() => setDrawerOpen(false)}
          />
          <button
            type="button"
            tabIndex={-1}
            aria-label="Close menu"
            onClick={closeDrawer}
            className="min-w-0 flex-1 cursor-default bg-black/50"
          />
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border bg-surface px-3 sm:px-4 short:h-12">
          {!desktop && (
            <Button
              ref={menuButton}
              variant="ghost"
              size="md"
              iconOnly
              aria-label="Open menu"
              aria-expanded={drawerShown}
              title="Menu (Alt+B)"
              onClick={() => setDrawerOpen(true)}
            >
              <Menu className="size-5" />
            </Button>
          )}
          <div className="min-w-0 flex-1">
            <ShiftBannerSlot />
          </div>
          {/* With shifts on, the shift banner carries the Cash out button; with shifts off the shell does. */}
          {!settings.shifts_enabled && (isAdmin || isCashier) && <CashOutButton />}
          <Button
            variant="ghost"
            size="md"
            iconOnly
            aria-label="Keyboard shortcuts"
            title="Keyboard shortcuts (?)"
            onClick={() => setHelpOpen(true)}
            className="hidden sm:inline-flex"
          >
            <Keyboard className="size-5" />
          </Button>
          <UserMenu />
        </header>
        <main id="main" tabIndex={-1} className="min-h-0 flex-1 overflow-y-auto p-4 outline-none">
          <ErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
      <CashPayoutHost />
      <ShortcutHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}
